import { useEffect, useRef, useState } from 'react';
import { api, type RenderJob } from '../lib/api';

/**
 * The render queue.
 *
 * GET /v1/renders has existed the whole time and no screen has ever shown it.
 * Settings fetched it for the budget and threw the jobs away — so everything
 * the endpoint works hardest for was computed on every request and discarded:
 * where a job sits in the queue, how many are ahead of it, whether its lease
 * expired under a worker that died, how much CPU it has burned.
 *
 * That is the screen a person needs when nothing appears to be happening. Its
 * absence is why "why is my video not rendering" had no answer inside the
 * product.
 */
const TONE: Record<RenderJob['status'], string> = {
  queued: 'var(--ink-2)',
  running: 'var(--blue)',
  complete: 'var(--green)',
  failed: 'var(--red)',
  cancelled: 'var(--ink-3)',
};

function ago(iso: string | null): string {
  if (!iso) return '—';
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

/** Says why a job is not moving, in the words a person would use. */
function why(j: RenderJob, limits: { max_concurrent_renders: number; running_now: number } | null): string | null {
  if (j.status === 'running' && j.lease_expired) {
    return 'the worker holding this stopped reporting — it will be picked up again';
  }
  if (j.status === 'queued' && j.queue && j.queue.aheadOfYou > 0) {
    return `${j.queue.aheadOfYou} job${j.queue.aheadOfYou === 1 ? '' : 's'} ahead of it`;
  }
  if (j.status === 'queued' && limits && Number(limits.running_now) >= limits.max_concurrent_renders) {
    return `this workspace already has ${limits.running_now} running, which is its limit`;
  }
  if (j.status === 'failed' && j.attempts > 1) {
    return `gave up after ${j.attempts} attempts`;
  }
  return null;
}

export function Renders() {
  const [jobs, setJobs] = useState<RenderJob[]>([]);
  const [limits, setLimits] = useState<{ max_concurrent_renders: number; running_now: number } | null>(null);
  const [budget, setBudget] = useState<{ usedMinutes: number; limitMinutes: number | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const live = useRef(false);

  async function load() {
    try {
      const r = await api.renders();
      setJobs(r.jobs); setLimits(r.limits); setBudget(r.budget); setError(null);
    } catch (e) {
      // Surfaced, not swallowed. An empty queue and a failed request look the
      // same on screen otherwise, and this is the screen people come to when
      // they already suspect something is wrong.
      setError((e as Error).message);
    } finally { setLoaded(true); }
  }

  useEffect(() => { void load(); }, []);

  // Polls only while something is actually in flight, and stops when it is not.
  // Schedule.tsx claims to do this in a comment and then polls forever.
  const inFlight = jobs.some((j) => j.status === 'queued' || j.status === 'running');
  useEffect(() => {
    live.current = inFlight;
    if (!inFlight) return;
    const t = setInterval(() => { if (live.current) void load(); }, 5000);
    return () => { live.current = false; clearInterval(t); };
  }, [inFlight]);

  return (
    <>
      <div className="vhead" data-move="render-failed">
        <div>
          <h2>Renders</h2>
          <p>
            Everything this workspace has asked the render plane to do. Refreshes while
            work is in flight and stops when it is not.
          </p>
        </div>
        <button className="btn" onClick={() => void load()}>Refresh</button>
      </div>

      {error && <div className="err">{error}</div>}

      {budget && (
        <div className="notice">
          {budget.limitMinutes === null
            ? 'Unmetered — no monthly ceiling on this workspace.'
            : `${budget.usedMinutes} of ${budget.limitMinutes} render minutes used this month.`}
          {limits && ` ${limits.running_now} of ${limits.max_concurrent_renders} running now.`}
        </div>
      )}

      <div className="panel">
        <div className="panel-hd"><h3>Queue</h3><span className="chip">{jobs.length}</span></div>
        {!loaded ? (
          <div className="empty">Loading…</div>
        ) : jobs.length === 0 ? (
          <div className="empty">
            Nothing has been rendered yet. Open a card, write its script, and press Render.
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>What</th><th>Status</th><th>Why</th>
                <th className="r">Attempts</th><th className="r">CPU</th><th>Started</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => {
                const reason = why(j, limits);
                return (
                  <tr key={j.id}>
                    <td>
                      <b>{j.title ?? j.kind ?? 'render'}</b>
                      <div className="sub-dim">
                        {j.kind ?? 'programme'}
                        {j.source_count > 0 && ` · cuts to ${j.source_count} source${j.source_count === 1 ? '' : 's'}`}
                      </div>
                    </td>
                    <td>
                      <span style={{ color: TONE[j.status], fontWeight: 600 }}>{j.status}</span>
                      {j.status === 'queued' && j.queue && (
                        <div className="sub-dim">position {j.queue.position}</div>
                      )}
                    </td>
                    <td style={{ color: 'var(--ink-2)', fontSize: 12.5 }}>
                      {j.error ? <span style={{ color: 'var(--red)' }}>{j.error}</span> : (reason ?? '—')}
                    </td>
                    <td className="r">{j.attempts}</td>
                    <td className="r">
                      {j.cpu_seconds ? `${Math.round(Number(j.cpu_seconds) / 60)}m` : '—'}
                    </td>
                    <td className="sub-dim">{ago(j.started_at ?? j.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
